import {
  DEFAULT_TAX_REGION,
  splitTaxIncludedAmount,
  TaxRegion,
} from '@repo/types/common/tax-region'
import {
  CustomerDiscounts,
  Product,
  ProductDetail,
  Promotion,
} from '@/hooks/use-cart-product/store'

export function formatAmountForDisplay(
  amount: number,
  currency: string
): string {
  const numberFormat = new Intl.NumberFormat(['es-ES'], {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'symbol',
  })
  return numberFormat.format(amount)
}

export function formatAmountForDisplayTrunk(
  amount: number,
  currency: string
): string {
  const hasDecimals = amount % 1 !== 0

  const numberFormat = new Intl.NumberFormat(['es-ES'], {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: hasDecimals ? 2 : 0, // Establecer 2 decimales si hay, 0 si no
    maximumFractionDigits: 2, // Asegurarse de tener hasta 2 decimales
  })

  return numberFormat.format(amount)
}

export function formatAmountForStripe(
  amount: number,
  currency: string
): number {
  const numberFormat = new Intl.NumberFormat(['en-US'], {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'symbol',
  })
  const parts = numberFormat.formatToParts(amount)
  let zeroDecimalCurrency: boolean = true
  for (const part of parts) {
    if (part.type === 'decimal') {
      zeroDecimalCurrency = false
    }
  }
  return zeroDecimalCurrency ? amount : Math.round(amount * 100)
}

const periodsTranslation = {
  '1d': 'diarios',
  '1m': 'mensual',
  '3m': 'trimestral',
  '6m': 'semestral',
  '1a': 'anual',
}
const periodsMontly = {
  '1d': 1 / 30,
  '1m': 1,
  '3m': 3,
  '6m': 6,
  '1a': 12,
}

export function getPeriodTranslation(productTime: string): string {
  return periodsTranslation[productTime] || null
}
export function getPeriodNumber(productTime: string): number {
  return periodsMontly[productTime] || 1
}

export type ProductCompare = {
  fixAmount: number
  amount: number
  period: number
}

export function getSaving(
  prod1: ProductCompare,
  prod2: ProductCompare
): number {
  const maxPeriod = Math.max(prod1.period, prod2.period)

  const maxAmount1 = (maxPeriod / prod1.period) * prod1.amount + prod1.fixAmount
  const maxAmount2 = (maxPeriod / prod2.period) * prod2.amount + prod2.fixAmount

  return maxAmount1 - maxAmount2
}

export function calculateProductDetail(products: Product[]): ProductDetail {
  return products.reduce(
    (totals, product) => {
      totals.totalAmount += product.amount

      if (product.period !== null) {
        totals.subscriptionAmount += product.amount
      }

      if (!totals.period && product.period !== null) {
        totals.period = product.period
        totals.productTime = product.productTime
      }

      return totals
    },
    {
      totalAmount: 0,
      subscriptionAmount: 0,
      period: '',
      productTime: '',
      productsList: [...products],
      canBeStudent: false,
      isElite: false,
    }
  )
}

/**
 *
 * @param from timestamp date in second
 * @return boolean true if date is in the past from now
 */
export function isDatePast(from: number) {
  return from * 1000 < Date.now()
}

/**
 * La región fiscal es un cuarto parámetro opcional: los cuatro llamadores del
 * store solo usan `total`, y el total no depende de la región (el importe
 * final es el mismo en Madrid y en Tenerife; lo que cambia es el reparto).
 */
export function getDiscount(
  prods: ProductDetail,
  promotion: Promotion,
  cDiscounts: CustomerDiscounts,
  taxRegion: TaxRegion = DEFAULT_TAX_REGION
) {
  const discounts = {
    promoDiscount: null,
    customerSubDiscount: 0,
    customerBalance: 0,
    subTotal: null,
    tax: null,
    total: null,
  }

  if (promotion?.code) {
    // Get total amount of products if only apply to specific products
    const amount =
      (promotion?.promoApplyToProducts || []).length === 0
        ? prods.totalAmount
        : (prods.productsList || []).reduce(
            (acc, prod) =>
              promotion.promoApplyToProducts.includes(prod.productId)
                ? acc + prod.amount
                : acc,
            0
          )

    if (promotion.promotionAmount)
      discounts.promoDiscount = Math.min(promotion.promotionAmount, amount)
    if (promotion.promotionPercentage)
      discounts.promoDiscount = Math.min(
        amount * promotion.promotionPercentage,
        amount
      )
  }
  if (cDiscounts?.customerSubDiscount || cDiscounts?.customerBalance) {
    if (prods.isElite) {
      discounts.customerSubDiscount = cDiscounts?.customerSubDiscount || 0
    }
    discounts.customerBalance = cDiscounts?.customerBalance || 0
  }

  const total =
    prods.totalAmount -
    discounts.promoDiscount +
    discounts.customerSubDiscount +
    discounts.customerBalance

  discounts.total = total < 0 ? 0 : total
  const { subtotal, tax } = splitTaxIncludedAmount(discounts.total, taxRegion)
  discounts.subTotal = subtotal
  discounts.tax = tax

  return discounts
}
